import {MeasurementStatus} from "../list/found-measurement";
import {MeasurementType} from "../measurement-models";
import {PatientData} from "../upload/upload-model";
import {UploadFormConfig} from "../upload/upload-form-config";
import {emptyUploadFormModel} from "../upload/upload-form-model";
import {EcgWorkflow, ExternalAnalysisType} from "../hospitals/edit-hospital/hospital.details";
import {PreanalysisEcgData} from "./finish-analysis.service";
import {SleepExternalFindings} from "./sleep-report/sleep-report-data";
import {FullPreanalysisEcgData} from "./ecg/generators/findings-generator/findings-generator.component";


export interface Analysis {
  readonly id: number;
  readonly uniqueId: string;
  readonly code: string;
  readonly uploadTime: string;
  readonly type: MeasurementType;
  readonly status: MeasurementStatus;
  readonly patientData: PatientData;
  readonly doctor: Doctor;
  readonly durationDays?: number;
  readonly startTime: string;
  readonly doctorFindings: DoctorFindings;
  readonly externalAnalysisUrl?: string;
  commentsCount?: number;
  readonly hospitalName: string;
  readonly analysisGenerationConfig: AnalysisGenerationConfig;
  readonly isChain: boolean;
  readonly ecgWorkflow: EcgWorkflow;
  readonly preanalysisEcgData?: PreanalysisEcgData;
  readonly fullPreanalysisEcgData?: FullPreanalysisEcgData;
  readonly isHome: boolean;
  readonly specifiedDurationDays?: number;
  readonly usesDateOfBirth?: boolean;
  readonly externalAnalysisType: ExternalAnalysisType;
  readonly reportLanguage: string;
  readonly deviceSerialNumber?: string;
  readonly bmi?: number;
  readonly essScore?: number;
  readonly sleepExternalFindings?: SleepExternalFindings;
}

export interface BpFindingsModel {
  avgSystolicBP: string[];
  avgDiastolicBP: string[];
  avgSystolicRange: string[];
  avgDiastolicRange: string[];
  systolicBPload: string[];
  diastolicBPload: string[];
  avgPuls: string[];
}

export interface DoctorFindings {
  findings?: string;
  conclusion?: string;
  patientStatusComments?: string;
  diagnosis?: string;
  symptomsComment?: string;
}

export interface Doctor {
  firstName: string;
  lastName: string;
  title?: string;
  city?: string;
}

export interface AnalysisGenerationConfig {
  findings_prevailingRhythm_sinus?: string;
  findings_prevailingRhythm_afib?: string;
  findings_prevailingRhythm_unknown?: string;

  findings_averageHeartRate?: string;
  findings_averageHeartRateWhenSinusPlusAfib?: string;
  findings_minimumHeartRate?: string;
  findings_minimumHeartRateSinusAndAtrial?: string;
  findings_maximumHeartRate?: string;
  findings_maximumHeartRateSinusAndAtrial?: string;
  findings_maximumHeartRateNotMaximumSinus?: string;

  findings_noPausesOverThreshold?: string;
  findings_onePauseOverThreshold?: string;
  findings_twoPausesOverThreshold?: string;
  findings_pausesOverThreshold?: string;

  findings_ventricularBeats?: string;
  findings_oneVentricularBeat?: string;
  findings_noVentricularBeats?: string;

  findings_noVentricularTachycardias?: string;
  findings_oneVentricularTachycardia?: string;
  findings_twoVentricularTachycardias?: string;
  findings_moreThanTwoVentricularTachycardias?: string;
  findings_oneVentricularTachycardiaWithLongestDurationLessOrEqualThan10?: string;
  findings_oneVentricularTachycardiaWithLongestDurationGreaterThan10?: string;
  findings_twoVentricularTachycardiasWithLongestDurationLessOrEqualThan10?: string;
  findings_twoVentricularTachycardiasWithLongestDurationGreaterThan10?: string;
  findings_twoVentricularTachycardiasWithMaxHrLessOrEqual10?: string;
  findings_twoVentricularTachycardiasWithMaxHrGreaterThan10?: string;
  findings_twoVentricularTachycardiasLongestIsFastest?: string;
  findings_twoVentricularTachycardiasLongestIsNotFastest?: string;
  findings_someVentricularTachycardiasWithLongestDurationNotGreaterThan10?: string;
  findings_someVentricularTachycardiasWithLongestDurationGreaterThan10?: string;
  findings_ventricularTachycardiasMaxHrDurationNotGreaterThan10?: string;
  findings_ventricularTachycardiasMaxHrDurationGreaterThan10?: string;
  findings_ventricularTachycardiasMaxHrEqualsLongestDurationMaxHr?: string;
  findings_ventricularTachycardiasLongestIsFastest?: string;
  findings_ventricularTachycardiasLongestIsNotFastest?: string;
  findings_twoVentricularTachycardiasMaxHrEqualsLongestDurationMaxHr?: string;
  findings_twoVentricularTachycardiasMaxHrNotLongestDurationMaxHrAndLongestDurationLessEqual10?: string;
  findings_twoVentricularTachycardiasMaxHrNotLongestDurationMaxHrAndLongestDurationGreaterThan10?: string;
  findings_oneVRun?: string;
  findings_moreThanOneVRun?: string;
  findings_moreThanOneVentricularTachycardias_additionalSentence?: string;

  findings_oneShortSlowVentricularRhythm?: string;
  findings_oneLongerSlowVentricularRhythm?: string;
  findings_twoShortSlowVentricularRhythm?: string;
  findings_twoLongerSlowVentricularRhythm?: string;
  findings_someShortSlowVentricularRhythm?: string;
  findings_someLongerSlowVentricularRhythm?: string;
  findings_oneSlowVentricularRhythm?: string;
  findings_moreThanOneSlowVentricularRhythm?: string;

  findings_noVentricularCouplets?: string;
  findings_oneVentricularCouplet?: string;
  findings_someVentricularCouplets?: string;

  findings_atrialBeats?: string;
  findings_oneAtrialBeat?: string;
  findings_noAtrialBeats?: string;
  findings_noAtrialFibrillation?: string;

  findings_noAtrialTachycardias?: string;
  findings_oneAtrialTachycardia?: string;
  findings_twoAtrialTachycardias?: string;
  findings_moreThanTwoAtrialTachycardias?: string;
  findings_oneAtrialTachycardiaWithLongestDurationLessOrEqualThan10?: string;
  findings_oneAtrialTachycardiaWithLongestDurationGreaterThan10?: string;
  findings_twoAtrialTachycardiaWithLongestDurationLessOrEqualThan10?: string;
  findings_twoAtrialTachycardiaWithLongestDurationGreaterThan10?: string;
  findings_atrialTachycardiasLongestDurationLessOrEqualThan10?: string;
  findings_atrialTachycardiasLongestDurationGreaterThan10?: string;
  findings_atrialTachycardiasMaxHrEqualsLongestDurationMaxHr?: string;
  findings_atrialTachycardiasMaxHrDurationNotGreaterThan10?: string;
  findings_atrialTachycardiasMaxHrDurationGreaterThan10?: string;
  findings_twoAtrialTachycardiasMaxHrEqualsLongestDurationMaxHr?: string;
  findings_twoAtrialTachycardiasMaxHrDurationNotGreaterThan10?: string;
  findings_twoAtrialTachycardiasMaxHrDurationGreaterThan10?: string;
  findings_twoAtrialTachycardiasLongestIsFastest?: string;
  findings_twoAtrialTachycardiasLongestIsNotFastest?: string;
  findings_moreThanTwoAtrialTachycardiasLongestIsFastest?: string;
  findings_moreThanTwoAtrialTachycardiasLongestIsNotFastest?: string;
  findings_oneSRun?: string;
  findings_moreThanOneSRun?: string;
  findings_moreThanOneAtrialTachycardias_additionalSentence?: string;

  findings_avConduction_normal?: string;
  findings_avConduction_otherAvb?: string;
  findings_deltaWave_normal?: string;
  findings_qrsWidth_normal?: string;
  findings_qtTime_normal?: string;
  findings_stSegment_normal?: string;

  findings_averageHeartRate_value?: string;
  findings_minimumHeartRate_value?: string;
  findings_maximumHeartRate_value?: string;
  findings_pausesOverThreshold_value?: string;
  findings_ventricularBeats_value?: string;
  findings_ventricularRuns_value?: string;
  findings_ventricularTachycardias_value?: string;
  findings_atrialBeats_value?: string;
  findings_supraventricularRuns_value?: string;
  findings_atrialTachycardias_value?: string;
  findings_slowVentricularRhythms_value?: string;

  patientStatusComments_reasonForStudy_arrhythmiaSensations?: string;
  patientStatusComments_reasonForStudy_atrialFibrilation?: string;
  patientStatusComments_reasonForStudy_other?: string;
  patientStatusComments_rhythmAffectingMedication_yes?: string;
  patientStatusComments_rhythmAffectingMedication_no?: string;
  patientStatusComments_symptomsDuringRecording_yes?: string;
  patientStatusComments_symptomsDuringRecording_no?: string;
  patientStatusComments_symptomsDuringRecording_noDiary?: string;

  diagnosis_i49_1?: string;
  diagnosis_i47_1?: string;
  diagnosis_i48_0?: string;
  diagnosis_i48_1?: string;
  diagnosis_i48_2?: string;
  diagnosis_i48_3?: string;
  diagnosis_i48_4?: string;
  diagnosis_i49_5?: string;
  diagnosis_z03_5?: string;
  diagnosis_i49_3?: string;
  diagnosis_i47_2?: string;
  diagnosis_i44_0?: string;
  diagnosis_i44_1?: string;
  diagnosis_i44_2?: string;
  diagnosis_i44_7?: string;
  diagnosis_i45_0?: string;
}

export function createUploadFormConfig(analysis: Analysis): UploadFormConfig {
  return new UploadFormConfig({
    model: {
      ...emptyUploadFormModel(),
      ...analysis.patientData,
    },
    usesDateOfBirth: analysis.usesDateOfBirth
  });
}

export function emptyDoctorFindings(): DoctorFindings {
  return {};
}


